import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { userState } from "@/lib/store";
import { getLockedPage, LockedPage, lockedPageUrl, StockRef } from "@/lib/lockedPages";
import { MetricPageJsonLd } from "@/components/json-ld/JsonLd";
import styles from "./AboutThisData.module.scss";

/**
 * "About this data": a short plain-language description of the page and
 * definitions of its metrics, rendered on the server.
 *
 * The data pages draw their numbers client-side, so before this the
 * signed-out HTML held little more than tab labels: nothing a search engine
 * could rank or an AI assistant could quote. This block puts the page's
 * meaning into the HTML a crawler receives, plus matching JSON-LD.
 *
 * It sits below the page (after <main>, inside the scrolling root), so it
 * never pushes a full-height grid around. Paying subscribers do not get it:
 * the server renders it before anyone is known, and it is dropped once a
 * paid plan is (below the fold, so there is no visible jump).
 *
 * One-screen pages (`aboutInPage` in lockedPages.ts, e.g. Index Rebalance) render it themselves with
 * `inPage`: a one-line disclosure at the foot of their layout that opens over the page, never below
 * it. Its text is in the server HTML either way.
 */

const PAID_PLANS = ["ORTEX Basic", "ORTEX Advanced", "ORTEX Institutional"];

/**
 * The intro, the definitions and the source line. A definition with a `question` is a heading of its
 * own (the question people search, e.g. "What is short interest?"), the rest a definition list.
 */
const body = (page: LockedPage, intro: string) => {
    const questions = page.definitions.filter((d) => d.question);
    const terms = page.definitions.filter((d) => !d.question);
    const isExternal = (href: string) => /^https?:\/\//.test(href);
    return (
        <>
            <p className={styles.intro}>{intro}</p>
            {questions.map((d) => (
                <div key={d.term} className={styles.question}>
                    <h2>{d.question}</h2>
                    <p>{d.definition}</p>
                </div>
            ))}
            {terms.length > 0 && (
                <dl className={styles.terms}>
                    {terms.map((d) => (
                        <div key={d.term} className={styles.term}>
                            <dt>{d.term}</dt>
                            <dd>{d.definition}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {page.links?.length ? (
                <nav className={styles.links} aria-label={page.linksTitle ?? "Related pages"}>
                    {page.linksTitle && <p className={styles.linksTitle}>{page.linksTitle}</p>}
                    <ul>
                        {page.links.map((link) => (
                            <li key={link.href}>
                                <a href={link.href} {...(isExternal(link.href) ? { rel: "noopener" } : {})}>
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            ) : null}
            {!page.aboutOnly && <p className={styles.source}>Source: ORTEX. Some figures are limited on the free view; live data and full history are part of ORTEX Advanced.</p>}
        </>
    );
};

const AboutThisData = ({ stock, inPage = false }: { stock?: StockRef; inPage?: boolean }) => {
    const { pathname } = useRouter();
    const user = useAtomValue(userState);
    const page = getLockedPage(pathname);
    if (!page) return null;
    if (!!page.aboutInPage !== inPage) return null;
    if (user?.subscription_plan && PAID_PLANS.includes(user.subscription_plan)) return null;

    const url = lockedPageUrl(pathname, stock);
    const name = stock ? `${stock.name} (${stock.exchangesymbol}:${stock.ticker}) ${page.metric}` : `${page.metric} on ORTEX`;
    const intro = page.intro(stock);

    const title = (
        <h2 id="about-this-data" className={styles.title}>
            About this data: {page.headline(stock)}
        </h2>
    );

    if (inPage) {
        return (
            <details className={styles.inPage}>
                <summary>{title}</summary>
                <section className={styles.inPageBody} aria-labelledby="about-this-data">
                    {body(page, intro)}
                    <MetricPageJsonLd url={url} name={name} description={intro} definitions={page.definitions} stock={stock} />
                </section>
            </details>
        );
    }

    return (
        <section className={styles.about} aria-labelledby="about-this-data">
            {title}
            {body(page, intro)}
            <MetricPageJsonLd url={url} name={name} description={intro} definitions={page.definitions} stock={stock} />
        </section>
    );
};

export default AboutThisData;
